import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import {
  appointmentReportsQueryKey,
  deleteAppointmentReport,
  downloadReportFile,
  listReports,
  MAX_APPOINTMENT_REPORT_FILES,
  PATIENT_APPTS_QUERY_KEY,
  updatePatientNote,
  uploadAppointmentReport,
} from "@/api/appointmentApi";
import type { Appointment, AppointmentReport } from "@/types/domain";

type Props = {
  appointment: Appointment;
};

export function AppointmentPatientContextCard({ appointment }: Props) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [noteDraft, setNoteDraft] = useState(appointment.patientNote ?? "");
  const [noteEditing, setNoteEditing] = useState(false);
  const [attachmentNoteDraft, setAttachmentNoteDraft] = useState("");
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  useEffect(() => {
    setNoteDraft(appointment.patientNote ?? "");
  }, [appointment.id, appointment.patientNote]);

  useEffect(() => {
    setNoteEditing(false);
  }, [appointment.id]);

  const canEdit = appointment.status === "PENDING" || appointment.status === "CONFIRMED";
  const canViewReports =
    appointment.status === "PENDING" ||
    appointment.status === "CONFIRMED" ||
    appointment.status === "COMPLETED";

  const reportsQuery = useQuery({
    queryKey: appointmentReportsQueryKey(appointment.id),
    queryFn: () => listReports(appointment.id),
    enabled: canViewReports,
  });

  const saveNoteMut = useMutation({
    mutationFn: () => updatePatientNote(appointment.id, noteDraft),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PATIENT_APPTS_QUERY_KEY });
      setNoteEditing(false);
    },
  });

  const uploadMut = useMutation({
    mutationFn: () =>
      uploadAppointmentReport(appointment.id, pendingFile!, attachmentNoteDraft || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: appointmentReportsQueryKey(appointment.id) });
      setPendingFile(null);
      setAttachmentNoteDraft("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
  });

  const deleteMut = useMutation({
    mutationFn: (reportId: string) => deleteAppointmentReport(appointment.id, reportId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: appointmentReportsQueryKey(appointment.id) });
    },
  });

  const reports: AppointmentReport[] = reportsQuery.data ?? [];
  const noteDirty = noteDraft !== (appointment.patientNote ?? "");
  const atReportLimit = reports.length >= MAX_APPOINTMENT_REPORT_FILES;

  const mutationError =
    saveNoteMut.isError || uploadMut.isError || deleteMut.isError ? (
      <Alert severity="error" sx={{ mb: 2 }}>
        Something went wrong. Please try again.
      </Alert>
    ) : null;

  return (
    <Box>
      {mutationError}

      <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ mb: 0.5 }}>
        Note for your doctor
      </Typography>
      {canEdit ? (
        noteEditing ? (
          <Stack spacing={1} sx={{ mb: 2 }}>
            <TextField
              multiline
              minRows={3}
              fullWidth
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="Symptoms, history, or questions…"
              disabled={saveNoteMut.isPending}
            />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="contained"
                disabled={!noteDirty || saveNoteMut.isPending}
                onClick={() => saveNoteMut.mutate()}
              >
                {saveNoteMut.isPending ? "Saving…" : "Save note"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={saveNoteMut.isPending}
                onClick={() => {
                  setNoteDraft(appointment.patientNote ?? "");
                  setNoteEditing(false);
                }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: "action.hover" }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {appointment.patientNote?.trim()
                  ? appointment.patientNote
                  : "No note yet. Use Edit to add symptoms, history, or questions for your doctor."}
              </Typography>
            </Paper>
            <Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setNoteDraft(appointment.patientNote ?? "");
                  setNoteEditing(true);
                }}
              >
                Edit
              </Button>
            </Box>
          </Stack>
        )
      ) : (
        <Typography variant="body2" sx={{ mb: 2 }}>
          {appointment.patientNote?.trim()
            ? appointment.patientNote
            : "No note was added for this appointment."}
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ mb: 0.5 }}>
        Attached documents
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Files you have uploaded for this appointment. Your doctor can download them after the visit is
        confirmed.
      </Typography>

      {reportsQuery.isLoading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading files…
          </Typography>
        </Box>
      )}

      {reportsQuery.isError && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          Could not load file list. Try refreshing the page.
        </Typography>
      )}

      {!reportsQuery.isLoading && reports.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No documents uploaded yet.
        </Typography>
      )}

      {reports.length > 0 && (
        <List dense disablePadding sx={{ mb: 1 }}>
          {reports.map((r) => (
            <ListItem
              key={r.id}
              secondaryAction={
                <Stack direction="row" spacing={0}>
                  <Tooltip title="Download">
                    <IconButton
                      edge="end"
                      aria-label={`Download ${r.fileName}`}
                      onClick={() => downloadReportFile(appointment.id, r.id, r.fileName)}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canEdit && (
                    <Tooltip title="Remove">
                      <span>
                        <IconButton
                          edge="end"
                          aria-label={`Remove ${r.fileName}`}
                          disabled={deleteMut.isPending}
                          onClick={() => {
                            if (window.confirm(`Remove “${r.fileName}” from this appointment?`)) {
                              deleteMut.mutate(r.id);
                            }
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              }
              sx={{ pr: canEdit ? 14 : 10, alignItems: "flex-start" }}
            >
              <ListItemText
                primary={r.fileName}
                secondary={
                  <>
                    {r.attachmentNote ? (
                      <Typography variant="body2" color="text.secondary" component="span" display="block">
                        {r.attachmentNote}
                      </Typography>
                    ) : null}
                    <Typography variant="caption" color="text.secondary" component="span" display="block">
                      {r.uploadedAt
                        ? format(new Date(r.uploadedAt), "d MMM yyyy, h:mm a")
                        : (r.contentType ?? "")}
                    </Typography>
                  </>
                }
                primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
              />
            </ListItem>
          ))}
        </List>
      )}

      {canEdit && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ mb: 0.5 }}>
            Add a document
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Optional note, then choose a file and upload. PDF, images, or Word — up to 15 MB each. Maximum{" "}
            {MAX_APPOINTMENT_REPORT_FILES} documents.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: "action.hover" }}>
            <Stack spacing={1.25}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ columnGap: 1, rowGap: 1 }}
              >
                <TextField
                  label="Note (optional)"
                  placeholder="Short note for this file…"
                  size="small"
                  value={attachmentNoteDraft}
                  onChange={(e) => setAttachmentNoteDraft(e.target.value)}
                  inputProps={{ maxLength: 2000 }}
                  disabled={atReportLimit || uploadMut.isPending}
                  sx={{ flex: "1 1 220px", minWidth: 160 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ ml: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                          {attachmentNoteDraft.length}/2000
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,application/pdf,image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    setPendingFile(f ?? null);
                    e.target.value = "";
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AttachFileIcon />}
                  disabled={atReportLimit || uploadMut.isPending}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ flexShrink: 0 }}
                  title={pendingFile ? `Selected: ${pendingFile.name}` : undefined}
                >
                  Choose file
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!pendingFile || atReportLimit || uploadMut.isPending}
                  onClick={() => uploadMut.mutate()}
                  sx={{ flexShrink: 0 }}
                  title={pendingFile ? pendingFile.name : undefined}
                >
                  {uploadMut.isPending ? "Uploading…" : "Add document"}
                </Button>
                {pendingFile && (
                  <Tooltip title={`Clear selected file (${pendingFile.name})`}>
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Clear selected file"
                        onClick={() => setPendingFile(null)}
                        disabled={uploadMut.isPending}
                        sx={{ flexShrink: 0 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
              {atReportLimit && (
                <Typography variant="body2" color="text.secondary">
                  You have reached the maximum of {MAX_APPOINTMENT_REPORT_FILES} documents for this appointment.
                </Typography>
              )}
            </Stack>
          </Paper>
        </>
      )}
    </Box>
  );
}
